import { useEffect, useMemo, useState } from "react";
import type { AppConfig, MagiMethod, LlmEntry } from "./config";
import { useTheme } from "./hooks/useTheme";
import { useDeliberation } from "./hooks/useDeliberation";
import { useTokenCapabilities } from "./hooks/useTokenCapabilities";
import { Header, type HealthState } from "./components/Header";
import { QuestionInput } from "./components/QuestionInput";
import { MethodSelector } from "./components/MethodSelector";
import { OptionListInput } from "./components/OptionListInput";
import { LLMSelector } from "./components/LLMSelector";
import { AdvancedOptions, type AdvancedValues } from "./components/AdvancedOptions";
import { DeliberationChamber } from "./components/DeliberationChamber";
import { VerdictOverlay } from "./components/VerdictOverlay";
import { RapporteurReport } from "./components/RapporteurReport";
import { ResponseCards } from "./components/ResponseCards";
import { InspectionDrawer } from "./components/InspectionDrawer";
import { DownloadActions } from "./components/DownloadActions";
import { DeliberativeOverview } from "./components/DeliberativeOverview";
import { ComposeRanking } from "./components/ComposeRanking";
import { methodRequiresOptions, METHODS_BY_ID, METHODS } from "./methods";
import { validateSubmission } from "./utils/validation";
import type { RunRequest, EffectiveLimits, TokenInfo } from "./types";

const FRONTEND_MAX_OPTIONS = 10;

export default function App({ config }: { config: AppConfig }) {
  const { theme, toggle } = useTheme(config.defaults.theme);
  const capabilities = useTokenCapabilities(config);

  const healthStatus: HealthState =
    capabilities.status === "loading"
      ? "checking"
      : capabilities.status === "ready"
      ? "online"
      : capabilities.kind === "unauthorized"
      ? "unauthorized"
      : "offline";

  return (
    <div className="app">
      <div className="bg-aura" aria-hidden data-testid="bg-aura" />
      <Header theme={theme} onToggleTheme={toggle} status={healthStatus} />

      <main className="app-main">
        {capabilities.status === "loading" && (
          <section
            className="panel scanlines"
            style={{ padding: 22, textAlign: "center" }}
            data-testid="capabilities-loading"
          >
            <div className="muted" style={{ fontSize: 14 }}>
              Discovering available models and capabilities…
            </div>
          </section>
        )}

        {capabilities.status === "error" && (
          <section
            className="panel scanlines alert"
            style={{ padding: 22 }}
            data-testid="capabilities-error"
            role="alert"
          >
            <h3 style={{ margin: 0, color: "var(--negative)" }}>
              {capabilities.kind === "unauthorized"
                ? "Service token rejected"
                : "MAGI-Service unreachable"}
            </h3>
            <p className="muted" style={{ fontSize: 14, marginTop: 10 }}>
              {capabilities.message}
            </p>
            <p className="muted" style={{ fontSize: 13, marginTop: 10 }}>
              {capabilities.kind === "unauthorized"
                ? "Provide a valid magi_-prefixed token via MAGI_SERVICE_TOKEN and redeploy, or create a new one in the MAGI-Service admin panel."
                : `Service URL: ${config.serviceBaseUrl}. Verify the backend is running and reachable from this browser.`}
            </p>
          </section>
        )}

        {capabilities.status === "ready" && (
          <ReadyApp config={config} info={capabilities.info} />
        )}
      </main>

      <footer
        style={{
          padding: "18px 28px",
          borderTop: "1px solid var(--border)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <a
          href="https://github.com/jason-chao/magi"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-magi-link"
          title="View MAGI on GitHub"
          style={{
            color: "var(--accent)",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            fontSize: 16,
            fontWeight: 500,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          <GithubMark />
          MAGI
        </a>
      </footer>
    </div>
  );
}

function ReadyApp({ config, info }: { config: AppConfig; info: TokenInfo }) {
  const { state, run, cancel } = useDeliberation(config);

  const effectiveLlms: LlmEntry[] = useMemo(
    () => info.allowed_llms.map((id) => synthesiseLlmEntry(id)),
    [info.allowed_llms],
  );

  const allowedMethods: MagiMethod[] = useMemo(
    () =>
      info.allowed_methods.length > 0
        ? info.allowed_methods
        : METHODS.map((m) => m.id),
    [info.allowed_methods],
  );

  const limits: EffectiveLimits = useMemo(
    () => ({
      maxPromptLength: info.max_user_prompt_length,
      maxSystemPromptLength: info.max_system_prompt_length,
      maxRapporteurPromptLength: info.max_rapporteur_prompt_length,
      maxOptionLength: info.max_option_length,
      maxLanguageLength: info.max_language_length,
      maxOptions: FRONTEND_MAX_OPTIONS,
    }),
    [info],
  );

  const defaultMethod: MagiMethod = allowedMethods[0] ?? "VoteYesNo";

  const [prompt, setPrompt] = useState("");
  const [method, setMethod] = useState<MagiMethod>(defaultMethod);
  const [options, setOptions] = useState<string[]>([]);
  const [selectedLlms, setSelectedLlms] = useState<string[]>(
    effectiveLlms.map((l) => l.id),
  );
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [formCollapsed, setFormCollapsed] = useState(false);
  const [advanced, setAdvanced] = useState<AdvancedValues>({
    deliberative: config.defaults.deliberative,
    voteThreshold: config.defaults.voteThreshold,
    allowAbstain: config.defaults.allowAbstain,
    systemPrompt: "",
    rapporteurPrompt: "",
    language: "",
  });

  // If the token's scope changes (hot reload / token swap), re-sync defaults.
  useEffect(() => {
    setSelectedLlms(effectiveLlms.map((l) => l.id));
  }, [effectiveLlms]);

  useEffect(() => {
    if (!allowedMethods.includes(method)) {
      setMethod(allowedMethods[0] ?? "VoteYesNo");
    }
  }, [allowedMethods, method]);

  const issues = useMemo(
    () =>
      validateSubmission({
        prompt,
        method,
        options,
        selectedLlms,
        rapporteurPrompt: advanced.rapporteurPrompt,
        systemPrompt: advanced.systemPrompt,
        language: advanced.language,
        limits,
        allowedMethods,
      }),
    [
      prompt,
      method,
      options,
      selectedLlms,
      advanced.rapporteurPrompt,
      advanced.systemPrompt,
      advanced.language,
      limits,
      allowedMethods,
    ],
  );
  const inFlight =
    state.phase !== "idle" && state.phase !== "complete" && state.phase !== "failed";

  useEffect(() => {
    if (state.phase === "complete") setSelectedModel(null);
  }, [state.phase]);

  function handleReset() {
    cancel();
    setPrompt("");
    setMethod(defaultMethod);
    setOptions([]);
    setSelectedLlms(effectiveLlms.map((l) => l.id));
    setSelectedModel(null);
    setAdvanced({
      deliberative: config.defaults.deliberative,
      voteThreshold: config.defaults.voteThreshold,
      allowAbstain: config.defaults.allowAbstain,
      systemPrompt: "",
      rapporteurPrompt: "",
      language: "",
    });
    setFormCollapsed(false);
  }

  function submit() {
    if (issues.length > 0 || inFlight) return;
    const req: RunRequest = {
      user_prompt: prompt.trim(),
      method,
      selected_llms: selectedLlms,
      deliberative: advanced.deliberative,
      method_options: buildMethodOptions(method, advanced, options),
      ...(advanced.systemPrompt.trim()
        ? { system_prompt: advanced.systemPrompt.trim() }
        : {}),
      ...(advanced.language.trim()
        ? { language: advanced.language.trim() }
        : {}),
    };
    setFormCollapsed(true);
    run(req);
  }

  const result = state.result;
  const lastRound = result?.rounds?.at(-1);
  const responses = lastRound?.responses ?? [];
  const roundErrors = lastRound?.errors ?? [];

  return (
    <>
      {formCollapsed ? (
        <CollapsedForm
          prompt={prompt}
          method={method}
          deliberative={advanced.deliberative}
          language={advanced.language}
          onExpand={() => setFormCollapsed(false)}
          onReset={handleReset}
        />
      ) : (
        <section className="stack panel scanlines" style={{ padding: 22 }}>
          <QuestionInput
            value={prompt}
            onChange={setPrompt}
            maxLength={limits.maxPromptLength}
            disabled={inFlight}
          />
          <MethodSelector
            value={method}
            onChange={setMethod}
            disabled={inFlight}
            allowedMethods={allowedMethods}
          />
          {methodRequiresOptions(method) && (
            <OptionListInput
              value={options}
              onChange={setOptions}
              maxOptions={limits.maxOptions}
              maxOptionLength={limits.maxOptionLength}
              disabled={inFlight}
            />
          )}
          <LLMSelector
            available={effectiveLlms}
            selected={selectedLlms}
            onChange={setSelectedLlms}
            disabled={inFlight}
          />
          <AdvancedOptions
            method={method}
            values={advanced}
            onChange={setAdvanced}
            disabled={inFlight}
            limits={limits}
            suggestedLanguages={config.suggestedLanguages}
          />
          {issues.length > 0 && (
            <ul
              style={{
                margin: 0,
                padding: "8px 12px",
                background: "rgba(226,75,74,0.05)",
                border: "1px solid var(--negative)",
                borderRadius: 8,
                fontSize: 12,
                listStyle: "none",
              }}
              data-testid="validation-issues"
            >
              {issues.map((i, k) => (
                <li key={k} style={{ color: "var(--negative)" }}>
                  · {i.message}
                </li>
              ))}
            </ul>
          )}
          <div
            className="row"
            style={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <button
              className="btn ghost"
              onClick={handleReset}
              data-testid="reset"
              title="Clear the question, options, and any result"
            >
              ⟲ RESET
            </button>
            <div className="row" style={{ gap: 8 }}>
              {inFlight && (
                <button
                  className="btn ghost"
                  onClick={() => {
                    cancel();
                    setFormCollapsed(false);
                  }}
                  data-testid="cancel"
                >
                  ⊗ CANCEL
                </button>
              )}
              <button
                className="btn primary"
                onClick={submit}
                disabled={inFlight || issues.length > 0}
                data-testid="submit"
              >
                {state.phase === "idle" || state.phase === "failed"
                  ? "▸ Initiate deliberation"
                  : state.phase === "complete"
                  ? "Re-deliberate"
                  : "Deliberating…"}
              </button>
            </div>
          </div>
        </section>
      )}

      {(state.phase !== "idle" || state.error) && (
        <div className="chamber-stack">
          <DeliberationChamber
            phase={state.phase}
            statusMessage={state.statusMessage}
            selectedLlms={selectedLlms}
            availableLlms={effectiveLlms}
            method={method}
            deliberative={advanced.deliberative}
            result={result}
            onSelectModel={setSelectedModel}
            selectedModel={selectedModel}
          />

          {state.error && (
            <div className="error-banner" role="alert" data-testid="error-banner">
              {state.error}
            </div>
          )}

          {result && state.phase === "complete" && (
            <>
              <VerdictOverlay method={method} result={result} />
              <DownloadActions
                input={{
                  prompt,
                  method,
                  deliberative: advanced.deliberative,
                  selectedLlms,
                  result,
                  timestamp:
                    state.completedAt !== null
                      ? new Date(state.completedAt)
                      : undefined,
                  availableLlms: effectiveLlms,
                }}
              />
              <RapporteurReport result={result} availableLlms={effectiveLlms} />
              {method === "Compose" && (
                <ComposeRanking result={result} availableLlms={effectiveLlms} />
              )}
              {method !== "Compose" && (
                <DeliberativeOverview
                  deliberative={advanced.deliberative}
                  result={result}
                />
              )}
              <ResponseCards
                method={method}
                responses={responses}
                errors={roundErrors}
                availableLlms={effectiveLlms}
                onSelect={setSelectedModel}
                selected={selectedModel}
              />
            </>
          )}
        </div>
      )}

      <InspectionDrawer
        modelId={selectedModel}
        result={result}
        availableLlms={effectiveLlms}
        onClose={() => setSelectedModel(null)}
      />
    </>
  );
}

function GithubMark() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path
        fillRule="evenodd"
        d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z"
      />
    </svg>
  );
}

function CollapsedForm({
  prompt,
  method,
  deliberative,
  language,
  onExpand,
  onReset,
}: {
  prompt: string;
  method: MagiMethod;
  deliberative: boolean;
  language: string;
  onExpand: () => void;
  onReset: () => void;
}) {
  const spec = METHODS_BY_ID[method];
  return (
    <section
      className="panel scanlines"
      data-testid="collapsed-form"
      style={{
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
      }}
      aria-label="Current question — click to edit and re-deliberate"
    >
      <div style={{ flex: 1, minWidth: 240 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--text-dim)",
          }}
        >
          Question · {spec?.label ?? method}
          {deliberative && " · deliberative"}
          {language.trim() && ` · ${language.trim()}`}
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: "var(--text)",
            marginTop: 2,
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3,
            overflow: "hidden",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
          title={prompt}
        >
          {prompt || "(no question)"}
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button
          className="btn ghost"
          onClick={onReset}
          data-testid="reset-collapsed"
          title="Clear the question, options, and any result"
        >
          ⟲ RESET
        </button>
        <button
          className="btn"
          onClick={onExpand}
          data-testid="expand-form"
          aria-expanded="false"
          aria-controls="deliberation-form"
        >
          ✎ Edit &amp; re-deliberate
        </button>
      </div>
    </section>
  );
}

function buildMethodOptions(
  method: MagiMethod,
  adv: AdvancedValues,
  options: string[],
): RunRequest["method_options"] {
  const mo: RunRequest["method_options"] = {};
  if (method === "VoteYesNo" || method === "VoteOptions") {
    mo.vote_threshold = adv.voteThreshold;
  }
  if (method === "VoteYesNo" || method === "Probability") {
    mo.allow_abstain = adv.allowAbstain;
  }
  if (method === "VoteOptions") {
    mo.options = options.map((o) => o.trim()).filter(Boolean);
  }
  if (adv.rapporteurPrompt.trim()) {
    mo.rapporteur_prompt = adv.rapporteurPrompt.trim();
  }
  return Object.keys(mo).length > 0 ? mo : undefined;
}

function synthesiseLlmEntry(id: string): LlmEntry {
  const slashIdx = id.indexOf("/");
  const providerPrefix = slashIdx > 0 ? id.slice(0, slashIdx) : "";
  const tail = slashIdx > 0 ? id.slice(slashIdx + 1) : id;

  const providerMap: Record<string, string> = {
    openai: "OpenAI",
    anthropic: "Anthropic",
    gemini: "Google",
    google: "Google",
    mistral: "Mistral",
    meta: "Meta",
    groq: "Groq",
    perplexity: "Perplexity",
    xai: "xAI",
    cohere: "Cohere",
  };
  const provider = providerMap[providerPrefix.toLowerCase()] ?? providerPrefix;

  const label = tail
    .split(/[-_/]/)
    .filter(Boolean)
    .map((part) => {
      if (/^\d[\d.]*$/.test(part)) return part;
      if (/^(gpt|llm|ai|xai)$/i.test(part)) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");

  return { id, label: label || id, provider: provider || undefined };
}
